/**
 * Normalize Axios error responses into user-facing strings.
 *
 * Reusable in every auth/registration screen. The three most common
 * shapes we have to handle:
 *  - 429 with Retry-After header  → tell the user the wait time
 *  - 4xx with {message, errors}   → surface the server message
 *  - network error (no response)  → generic "check your connection"
 *
 * Returns an object with:
 *   message     - string to show in the snackbar
 *   isThrottled - true for HTTP 429 so the caller can start a local
 *                  button cooldown instead of letting the user hammer
 *                  it and compound the throttle
 *   retryAfterSec - parsed Retry-After in seconds, 0 if unknown
 *   status      - the raw HTTP status if present
 */
export function apiErrorMessage(errorResponse, fallback = "Something went wrong. Please try again.") {
    const status = errorResponse?.response?.status;
    const headers = errorResponse?.response?.headers ?? {};
    const serverMessage = errorResponse?.response?.data?.message;

    if (status === 429) {
        const retryAfterSec = Number(
            headers['retry-after'] ?? headers['Retry-After'] ?? 0,
        );
        const minutes = retryAfterSec > 0 ? Math.ceil(retryAfterSec / 60) : null;
        const message = minutes
            ? `Too many attempts. Try again in about ${minutes} minute${minutes > 1 ? 's' : ''}.`
            : "Too many attempts. Please wait a few minutes and try again.";
        return { message, isThrottled: true, retryAfterSec, status };
    }

    // No response object = network error (CORS, offline, DNS, etc.)
    if (!errorResponse?.response) {
        return {
            message: "Can't reach the server. Check your connection and try again.",
            isThrottled: false,
            retryAfterSec: 0,
            status: null,
        };
    }

    // Server returned an explicit message → surface it verbatim (Laravel
    // validation errors put the human-readable text here).
    return {
        message: serverMessage ?? fallback,
        isThrottled: false,
        retryAfterSec: 0,
        status,
    };
}

/**
 * Convenience wrapper that routes the normalized message to a snackbar
 * and returns the parsed info in case the caller needs the cooldown
 * number.
 */
export function showApiError(snackbar, errorResponse, fallback) {
    const info = apiErrorMessage(errorResponse, fallback);
    snackbar.showSnackbar(info.message, 'error');
    return info;
}
